<template>
  <transition name="el-fade-in">
    <div v-if="visible" class="el-image-viewer__wrapper">
      <!-- 遮罩层 -->
      <div class="el-image-viewer__mask" @click="close"></div>

      <!-- 主体区域 -->
      <div
        class="el-image-viewer__canvas"
        @wheel.prevent="handleWheel"
        @mousedown="startDrag"
        @mousemove="onDrag"
        @mouseup="endDrag"
        @mouseleave="endDrag"
        @dblclick="toggleZoom"
      >
        <img
          :src="currentSrc"
          ref="img"
          :style="{
            transform: 'translate(' + offsetX + 'px,' + offsetY + 'px) scale(' + scale + ') rotate(' + rotate + 'deg)',
            transition: dragging ? 'none' : 'transform 0.15s ease'
          }"
        />
      </div>

      <!-- 工具栏（悬浮在图片下方居中） -->
      <div class="el-image-viewer__toolbar">
        <span class="el-image-viewer__indicator">{{ currentIndex + 1 }} / {{ images.length }}</span>
        <div class="el-image-viewer__actions">
          <span class="el-image-viewer__action" @click="rotateLeft">⟲</span>
          <span class="el-image-viewer__action" @click="rotateRight">⟳</span>
          <span class="el-image-viewer__action" @click="zoomOut">－</span>
          <span class="el-image-viewer__action" @click="zoomIn">＋</span>
          <span class="el-image-viewer__action" @click="resetZoom">⟲</span>
          <span class="el-image-viewer__action" @click="close">✕</span>
        </div>
      </div>

      <!-- 左右切换按钮 -->
      <button class="el-image-viewer__btn el-image-viewer__prev" @click="prev">‹</button>
      <button class="el-image-viewer__btn el-image-viewer__next" @click="next">›</button>
    </div>
  </transition>
</template>

<script>
import { Message } from 'element-ui'

export default {
  name: 'ImagePreviewer',
  data () {
    return {
      visible: false,
      images: [],
      currentIndex: 0,
      scale: 1,
      rotate: 0,
      offsetX: 0,
      offsetY: 0,
      dragging: false,
      dragStartX: 0,
      dragStartY: 0,
      lastEdgeType: null,
      lastEdgeTime: 0
    }
  },
  computed: {
    currentSrc () {
      return this.images[this.currentIndex]
    }
  },
  methods: {
    open (images, index = 0) {
      this.images = images
      this.currentIndex = index
      this.visible = true
      this.resetZoom()
      // 绑定键盘监听
      window.addEventListener('keydown', this.handleKeydown)
    },
    close () {
      this.visible = false
      // 解绑键盘监听，防止内存泄漏
      window.removeEventListener('keydown', this.handleKeydown)
    },
    handleKeydown (e) {
      if (!this.visible) return

      switch (e.key) {
        case 'ArrowLeft': // 上一张
          this.prev()
          break
        case 'ArrowRight': // 下一张
          this.next()
          break
        case 'ArrowUp': // 放大
          this.zoomIn()
          break
        case 'ArrowDown': // 缩小
          this.zoomOut()
          break
        case 'Escape': // 关闭
          this.close()
          break
      }
    },
    next () {
      if (this.currentIndex < this.images.length - 1) {
        this.currentIndex++
        this.resetZoom()
      } else {
        this.showEdge('last')
      }
    },
    prev () {
      if (this.currentIndex > 0) {
        this.currentIndex--
        this.resetZoom()
      } else {
        this.showEdge('first')
      }
    },
    showEdge (type) {
      const now = Date.now()
      if (this.lastEdgeType === type && now - this.lastEdgeTime < 1500) return
      this.lastEdgeType = type
      this.lastEdgeTime = now
      const text = type === 'first' ? '已经是第一张图片' : '已经是最后一张图片'
      const msg = Message.info({
        message: text,
        duration: 1500,
        offset: 20
      })

      // 手动改 z-index（Element UI 默认 2000，这里提升到比预览器 9999 高一点）
      if (msg.$el) {
        msg.$el.style.zIndex = '10010'
      }
    },
    rotateLeft () {
      this.rotate -= 90
    },
    rotateRight () {
      this.rotate += 90
    },
    zoomIn () {
      this.scale = Math.min(this.scale + 0.2, 3)
    },
    zoomOut () {
      this.scale = Math.max(this.scale - 0.2, 0.5)
    },
    resetZoom () {
      this.scale = 1
      this.rotate = 0
      this.offsetX = 0
      this.offsetY = 0
    },
    toggleZoom () {
      if (this.scale === 1) {
        this.scale = 2
      } else {
        this.resetZoom()
      }
    },
    handleWheel (e) {
      const rect = e.currentTarget.getBoundingClientRect()
      const imgX = e.clientX - rect.left - rect.width / 2
      const imgY = e.clientY - rect.top - rect.height / 2

      const oldScale = this.scale
      let newScale = oldScale
      if (e.deltaY < 0) {
        newScale = Math.min(oldScale + 0.2, 3)
      } else {
        newScale = Math.max(oldScale - 0.2, 0.5)
      }

      this.offsetX -= imgX * (newScale / oldScale - 1)
      this.offsetY -= imgY * (newScale / oldScale - 1)
      this.scale = newScale
    },
    startDrag (e) {
      if (this.scale <= 1) return
      this.dragging = true
      this.dragStartX = e.clientX - this.offsetX
      this.dragStartY = e.clientY - this.offsetY
    },
    onDrag (e) {
      if (!this.dragging) return
      this.offsetX = e.clientX - this.dragStartX
      this.offsetY = e.clientY - this.dragStartY
    },
    endDrag () {
      this.dragging = false
    }
  }
}
</script>

<style scoped>
.el-image-viewer__wrapper {
  position: fixed;
  inset: 0;
  z-index: 5555;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}
.el-image-viewer__mask {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.85);
}
.el-image-viewer__canvas {
  max-width: 80%;
  max-height: 80%;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
}
.el-image-viewer__canvas img {
  max-width: 100%;
  max-height: 100%;
  cursor: grab;
}
.el-image-viewer__canvas img:active {
  cursor: grabbing;
}

/* 工具栏（悬浮在图片下方居中） */
.el-image-viewer__toolbar {
  position: absolute;
  bottom: 30px; /* 悬浮在图片下方 */
  left: 50%;
  transform: translateX(-50%);
  z-index: 20001;
  display: flex;
  flex-direction: column;
  align-items: center;
  color: #fff;
}
.el-image-viewer__indicator {
  font-size: 14px;
  margin-bottom: 8px;
}
.el-image-viewer__actions {
  display: flex;
  transform: translateX(-0%);
  padding-top: 8px;
}
.el-image-viewer__action {
  width: 36px;
  height: 36px;
  line-height: 36px;
  text-align: center;
  border-radius: 50%;
  margin: 0 6px;
  background: rgba(0, 0, 0, 0.6);
  cursor: pointer;
  user-select: none;
  font-size: 16px;
  transition: background 0.2s;
}
.el-image-viewer__action:hover {
  background: rgba(0, 0, 0, 0.9);
}

/* 左右切换按钮 */
.el-image-viewer__btn {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  font-size: 32px;
  background: rgba(0, 0, 0, 0.5);
  border: none;
  color: #fff;
  cursor: pointer;
  padding: 5px 12px;
  border-radius: 50%;
  z-index: 20001;
}
.el-image-viewer__prev {
  left: 15px;
}
.el-image-viewer__next {
  right: 15px;
}
</style>
